import {
  AutoComplete,
  FormContainer,
  ISelectType,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { Grid } from "@mui/material";
import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IProfileListCategory } from "../../../../../../models/profile-list-category";
import MainApi from "../../../../../../services/mainApi.service";
import {
  createProfileListCategories,
  updateProfileListCategories,
} from "../../../../../../services/profileListCategories.service";
import {
  profileListCategorySchema,
  TProfileListCategoryForm,
} from "./profileListCategorySchema";

const translationPath = "page.register.roles.";

const LIST_CATEGORY_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/ListCategory/";
const listCategoryService = new MainApi<ISelectType>(LIST_CATEGORY_ROUTE);

interface IProfileListCategoryProps {
  id?: string;
  roleId?: string;
  rows: IProfileListCategory[];
  onBack: () => void;
}

export const ProfileListCategory = memo(
  ({ id, roleId, rows, onBack }: IProfileListCategoryProps) => {
    const { t } = useTranslation();

    const { sendRequest: create, loading: cLoading } = useFetch(
      createProfileListCategories
    );

    const { sendRequest: update, loading: uLoading } = useFetch(
      updateProfileListCategories
    );

    const row = useMemo(
      () => rows.find((currentRow) => currentRow.id === id) ?? null,
      [rows, id]
    );

    const getHandler = useCallback(async (): Promise<
      Treturn<TProfileListCategoryForm>
    > => {
      const formData: TProfileListCategoryForm = {
        listCategory: row
          ? { id: row.listCategory.id, name: row.listCategory.name }
          : null,
      };

      return { data: formData, success: true };
    }, [row]);

    const updateCategoryIds = useCallback(
      (categoryId: string) =>
        Array.from(
          new Set(
            rows.map((currentRow) =>
              currentRow.id === row?.id ? categoryId : currentRow.listCategoryId
            )
          )
        ),
      [row?.id, rows]
    );

    const saveHandler = (v: TProfileListCategoryForm) =>
      create({
        roleId: roleId ?? "",
        listCategoryIds: v.listCategory ? [v.listCategory.id] : [],
      });

    const updateHandler = (v: TProfileListCategoryForm) =>
      update({
        id: roleId ?? "",
        item: {
          roleId: roleId ?? "",
          listCategoryIds: v.listCategory
            ? updateCategoryIds(v.listCategory.id)
            : rows.map((currentRow) => currentRow.listCategoryId),
        },
      });

    const loading = cLoading || uLoading;

    return (
      <FormContainer<TProfileListCategoryForm>
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        getFunction={getHandler}
        loading={loading}
        title={t(translationPath + "list_category")}
        schema={profileListCategorySchema}
        isModalForm={true}
        subtitle={
          id
            ? t(translationPath + "edit_link")
            : t(translationPath + "add_list_category")
        }
        backButtonOnClick={onBack}
        redirectOnSave={false}
        onSuccess={onBack}
        propId={id}
        formActionsOnBottom={false}
        fields={() => (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <AutoComplete
                request={listCategoryService.list}
                name="listCategory"
                size="small"
                getOptionLabel={(option: ISelectType) => option?.name ?? ""}
                label={t(translationPath + "list_category")}
              />
            </Grid>
          </Grid>
        )}
      />
    );
  }
);
